import { ValidationRule, ValidationRules } from './base-form-service';

// Descreve um campo de formulario: rotulo, obrigatoriedade, default e saneador opcional.
export type FieldConfig<T extends Record<string, any>, K extends keyof T> = {
    label: string;
    required?: boolean;
    defaultValue?: T[K];
    sanitize?: (value: T[K]) => T[K];
};

export type FieldConfigMap<T extends Record<string, any>> = {
    [K in keyof T]: FieldConfig<T, K>;
};

// Helper to build a typed FieldConfigMap from a partial definition, defaulting labels to the field key.
export const buildFieldConfigMap = <T extends Record<string, any>>(
    config: Partial<Record<keyof T, Partial<FieldConfig<T, keyof T>>>>,
): FieldConfigMap<T> =>
    (Object.entries(config) as Array<[keyof T, Partial<FieldConfig<T, keyof T>>]>).reduce((acc, [key, cfg]) => {
        acc[key] = {
            label: cfg.label ?? String(key),
            required: cfg.required ?? false,
            defaultValue: cfg.defaultValue as T[typeof key],
            sanitize: cfg.sanitize as FieldConfig<T, keyof T>['sanitize'],
        } as FieldConfig<T, keyof T>;
        return acc;
    }, {} as FieldConfigMap<T>);

type ReducerAccumulator<T extends Record<string, any>, R> = Partial<Record<keyof T, R>>;

type ConfigMapper<T extends Record<string, any>, R> = <K extends keyof T>(key: K, cfg: FieldConfig<T, K>) => [K, R] | undefined;

// Aplica um mapper sobre FIELD_CONFIG e devolve um objeto parcial com os resultados.
export const reduceConfig = <T extends Record<string, any>, R>(
    config: FieldConfigMap<T>,
    mapper: ConfigMapper<T, R>,
): ReducerAccumulator<T, R> =>
    (Object.entries(config) as Array<[keyof T, FieldConfig<T, keyof T>]>).reduce((acc, [key, cfg]) => {
        const mapped = mapper(key as keyof T, cfg as FieldConfig<T, keyof T>);
        if (mapped) {
            acc[mapped[0]] = mapped[1];
        }
        return acc;
    }, {} as ReducerAccumulator<T, R>);

// Construtor de objeto de defaults a partir do mapa de campos.
export const buildDefaults = <T extends Record<string, any>>(config: FieldConfigMap<T>) =>
    reduceConfig<T, T[keyof T] | undefined>(config, (key, cfg) => [key, cfg.defaultValue]) as T;

// Construtor de funcao de sanitizacao que aplica os saneadores de cada campo.
export const buildSanitizer = <T extends Record<string, any>>(config: FieldConfigMap<T>) => (input: Partial<T>) =>
    reduceConfig<T, T[keyof T]>(config, (key, cfg) => {
        const value = input[key];
        return [key, cfg.sanitize ? cfg.sanitize(value as T[typeof key]) : (value as T[typeof key])];
    }) as Partial<T>;

// Construtor de regras obrigatorias a partir do mapa de campos.
export const buildRequiredRules = <T extends Record<string, any>>(
    config: FieldConfigMap<T>,
    messageFor?: <K extends keyof T>(key: K, cfg: FieldConfig<T, K>) => string,
) =>
    reduceConfig<T, ValidationRule<T, keyof T>[] | undefined>(config, <K extends keyof T>(key: K, cfg: FieldConfig<T, K>) => {
        if (!cfg.required) return undefined;
        const message = messageFor ? messageFor(key, cfg) : `O campo precisa ser preenchido${cfg.label ? `: ${cfg.label}` : ''}`;
        const requiredRule = ValidationRules.required<T, K>(message) as ValidationRule<T, keyof T>;
        return [key, [requiredRule]];
    }) as ValidationRules<T>;
